import { useEffect, useState, useRef } from 'react';
import { Link } from "react-router";
import menu_model from "../../../models/menu_model";
import user_model from "../../../models/user_model";
import { useUser } from "../../../xAppLib/Hooks";
import UserPic from "../../../xAppLib/Users/UserPic";
import { getUserDisplayName } from "helpers/getUserDisplayName";
import SIcon from "../../NUI/StreamlineIcons/SIcon";
import { Dialog } from "@headlessui/react";
import { Header } from "./Header/Header";
import { cls } from "../../NUI/utils";
import { useNotifications } from '@iscr/notifications/hooks/useNotifications';
import NotificationsListApp from '@iscr/notifications/view/app/NotificationsListApp';
import NotificationIndicator from '@iscr/notifications/view/NotificationIndicator';
import { useDisclosure } from '../../../xAppLib/Hooks/useDisclosure';

export function MainMenuItem({ icon, label, onClick, to, clsn="", children, active, testId = "account-menu-item", header, ...rest }) {
	const itemProps = onClick ? { onClick, type: "button" } : { to: to };
	const Item = onClick ? "button" : Link;
	return (
		<Item {...itemProps} {...rest} data-testid={testId} className={`item menu-item flex items-center transition ${clsn} ${active?"active":""}`}>
			{icon && <SIcon name={icon} size="s" cls="text-is2-gray-body p-1" bold />}
			{children ?? label}
		</Item>
	);
}

export function UserMenuItem({ name, trigger, icon }) {
	const acc_menu = menu_model.get_accountmenu();
	const user = useUser()
	const btnRef = useRef(null);
	const [align, setAlign] = useState(0);
	const {unreadCount, notificationsPanel, setNotificationsPanel} = useNotifications();
	const { isOpen, onClose, onOpen } = useDisclosure({
		...(name === "Notifications" && {
			isOpenProp: notificationsPanel?.open,
			onCloseCallback: () => {
				setNotificationsPanel(prev => ({...prev, open: false }))
				setAlign(0)
			}
		}),
	});

	const handleAlign = (e) => {
		if(!btnRef.current) btnRef.current = e.currentTarget;
		const curr = btnRef.current?.getBoundingClientRect();
		if(curr) {
			const aligned = window.innerWidth > 992 ? (curr.left + curr.width / 2) + "px" : 0;
			setAlign(aligned);
		}
	}

	useEffect(() => {
		const handleResize = () => {
			if(isOpen) handleAlign()
		}
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [isOpen]);

	const defaultName = getUserDisplayName(user, {
		format: 'first',
		defaultValue: 'User'
	})

	const menuConfig = {
		Account: {
			button: <button className={cls("item dropdown", isOpen ? "active" : "focus-visible:ring-1 focus-visible:ring-white")} data-testid={`dropdown-${name}`} onClick={(e) => {
						onOpen() 
						handleAlign(e)
					}}>
						<>
							<span className="hidden lg:block">{trigger === "undefined u" ? defaultName : trigger || trigger === "undefined" ? defaultName : trigger || name}</span>
							<SIcon name={icon} cls="p-[9px]" bold svg_cls="h-[18px] w-[18px]" />
						</>
					</button>,
			content: 	<div data-testid="dialog-account-menu" className="px-6 pt-6 pb-12 lg:pb-3">
							<div className="flex items-center gap-2 pb-[4px] mb-4">
								<div className="!border !border-white !rounded-full">
									<UserPic
										photoURL={user?.user_det?.photoURL}
										email={user?.user_det?.email}
										name={getUserDisplayName(user, {
											format: 'full',
											defaultValue: 'User'
										})}
										size={40}
										className="!rounded-full !border !border-white !bg-cover !bg-no-repeat"
									/>
								</div>
								<h3 className="text-[20px] leading-[28px] font-medium !mt-0">
									{getUserDisplayName(user, {
										format: 'full',
										defaultValue: 'User'
									})}</h3>
							</div>

							<div className="menu-items" onClick={onClose}>
								{acc_menu.slice(0, -4).map((el, i) => <MainMenuItem key={i} icon={el.icon} to={el.link} label={el.name} />)}

								{app.acl.is_mang && app.acl.is_cosm && <MainMenuItem icon='building' to='/org/my-clinic-profile' content="Clinic Profile" />}
								{app.acl.is_nurse && <MainMenuItem icon='certificate' to='/cosm/audit/list/practitioner' content="Audit history" />}
								{(app.acl.is_admin || app.acl.is_nurse || app.acl.is_doc) && (
									<MainMenuItem icon='video-play' to='/vidcall-troubleshooting' label="Test VidCall" />
								)}

								<h3 className="font-medium text-[20px] leading-[28px] mb-4 mt-6">Account</h3>
								{acc_menu.slice(-4).map((el, i) => <MainMenuItem key={i} icon={el.icon} to={el.link} label={el.name} />)}

								<MainMenuItem label="Sign out" icon="logout" onClick={() => user_model.logout()} />
							</div>
						</div>
		},
		Notifications: {
			button: <NotificationIndicator
						open={isOpen}
						onClick={(e) => {
							setNotificationsPanel(prev => ({...prev, open: true })),
							handleAlign(e)
						}}
						unreadCount={unreadCount}
					/>,
			content: <NotificationsListApp onClose={onClose} />
		}
	}

	return <>
			{menuConfig[name]?.button}

			<Dialog
				open={isOpen}
				onClose={onClose}
				className="relative"
			>
				<div className="fixed inset-0 flex lg:w-[332px] lg:h-fit items-center justify-center mt-[56px] lg:mt-[80px] z-20 lg:shadow-lg lg:rounded-b-[28px] lg:-translate-x-1/2" style={{ left: align }}>
					<div className="absolute -top-[10px] left-1/2 -translate-x-1/2 hidden shrink-0 w-0 h-0 lg:block border-x-transparent border-x-[10px] border-b-white border-b-[10px]"></div>
					<Dialog.Panel className={cls(`user-menu-container relative transform  bg-white w-full lg:rounded-b-[28px] overflow-hidden`)}>
						<div className={cls("h-[calc(100dvh-56px)] lg:h-fit lg:max-h-[88vh]", name == "Account" && "overflow-y-auto")}>
							<Header title={name} clsn="!flex-row justify-between lg:hidden" onClose={onClose}></Header>
							{menuConfig[name]?.content}
						</div>
					</Dialog.Panel>
				</div>
			</Dialog>
	</>
}
